import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';

import { StoreProductsPage } from 'utils/types/storeProduct';
import { mapStoreProductToCatalogueItem } from 'utils/mappers/storeProduct';
import { dedupeByProduct } from 'utils/productGroup';
import { useApiAuth } from './useApiAuth';

const MIN_CHARS = 2;
const MAX_RESULTS = 6;
const DEBOUNCE_MS = 350;

// Debounce simple: solo actualiza el valor tras `delay` ms sin cambios.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Búsqueda en vivo de premios (autocomplete). Debounced para no pegarle al
// backend en cada tecla. Usa el endpoint versionado /app/v1/store/products
// (búsqueda híbrida: exacto id/código + léxico trigram + semántico, acotada a
// lo disponible para el participante).
export function useAwardSearch(term: string) {
  const { get } = useApiAuth();
  const debounced = useDebouncedValue(term.trim(), DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_CHARS;

  const query = useQuery({
    queryKey: ['store', 'award-search', debounced],
    queryFn: async () => {
      const qs = queryString.stringify({ term: debounced, limit: MAX_RESULTS });
      const page = await get<StoreProductsPage>(`/lala4/app/v1/store/products?${qs}`);
      return dedupeByProduct((page.items ?? []).map(mapStoreProductToCatalogueItem));
    },
    enabled,
    staleTime: 60_000,
  });

  return {
    results: query.data ?? [],
    isLoading: enabled && query.isFetching,
    // true cuando el término ya alcanza el mínimo (para mostrar el dropdown).
    isActive: enabled,
  };
}
